import { useCallback } from "react";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { storage } from "@data/firebase";
import { useAuth } from "@contexts/AuthContext";
import { useProjects } from "@contexts/ProjectContext";
import { projectStorageBasePath } from "@domain/project/paths";
import useFirestoreDoc from "@data/hooks/useFirestoreDoc";
import { resizeImage } from "@shared/utils/imageResize";
import { fetchLinkPreview } from "@shared/utils/linkPreview";

/**
 * Manages the user's Design gallery items.
 *
 * Items are stored as an array on the per-user Firestore doc
 *   users/{uid}/data/design-items   (shape: { value: [...] })
 *
 * Photo files live in Storage at
 *   users/{uid}/design/{itemId}.jpg
 */
export default function useDesignItems() {
  const { user } = useAuth();
  const { activeProject } = useProjects();
  const [items, setItems, loaded] = useFirestoreDoc("design-items", []);

  const addPhoto = useCallback(
    async (file, { caption = "", tags = [] } = {}) => {
      if (!user || !activeProject || !file) return;
      const resized = await resizeImage(file, 1600, 0.85);
      const id = crypto.randomUUID();
      const path = `${projectStorageBasePath(activeProject)}/design/${id}.jpg`;
      const sref = storageRef(storage, path);
      await uploadBytes(sref, resized);
      const url = await getDownloadURL(sref);
      const item = {
        id,
        type: "photo",
        imageUrl: url,
        storagePath: path,
        caption,
        tags,
        createdAt: Date.now(),
      };
      setItems((prev) => [item, ...(prev || [])]);
      return item;
    },
    [user, activeProject, setItems]
  );

  const addLink = useCallback(
    async (url, { caption = "", tags = [], imageUrl: overrideImage, title: overrideTitle, skipPreview = false } = {}) => {
      if (!user || !url) return;
      let preview = { imageUrl: null, title: "", description: "" };
      if (!skipPreview) {
        try {
          preview = await fetchLinkPreview(url);
        } catch (err) {
          console.warn("[useDesignItems] link preview failed", err);
        }
      }
      const id = crypto.randomUUID();
      const item = {
        id,
        type: "link",
        imageUrl: overrideImage || preview.imageUrl || "",
        sourceUrl: url,
        title: overrideTitle || preview.title || url,
        caption: caption || preview.description || "",
        tags,
        createdAt: Date.now(),
      };
      setItems((prev) => [item, ...(prev || [])]);
      return item;
    },
    [user, setItems]
  );

  const updateItem = useCallback(
    (id, patch) => {
      setItems((prev) =>
        (prev || []).map((it) => (it.id === id ? { ...it, ...patch } : it))
      );
    },
    [setItems]
  );

  /**
   * Attach (or replace) the image on an existing item — used when a link
   * preview couldn't be auto-fetched and the user wants to add their own image.
   */
  const attachImage = useCallback(
    async (itemId, file) => {
      if (!user || !activeProject || !file) return;
      const resized = await resizeImage(file, 1600, 0.85);
      // Reuse the same storage path scheme; suffix with timestamp so we don't
      // collide with any prior file at this id and so the URL changes (forces
      // browsers to refetch instead of showing a stale cached image).
      const path = `${projectStorageBasePath(activeProject)}/design/${itemId}-${Date.now()}.jpg`;
      const sref = storageRef(storage, path);
      await uploadBytes(sref, resized);
      const url = await getDownloadURL(sref);

      // If the item already had a stored photo, delete the old file
      let oldPath = null;
      setItems((prev) => {
        const next = (prev || []).map((it) => {
          if (it.id !== itemId) return it;
          if (it.storagePath) oldPath = it.storagePath;
          return { ...it, imageUrl: url, storagePath: path };
        });
        return next;
      });
      if (oldPath) {
        try {
          await deleteObject(storageRef(storage, oldPath));
        } catch (err) {
          console.warn("[useDesignItems] old image cleanup failed", err);
        }
      }
    },
    [user, activeProject, setItems]
  );

  const removeItem = useCallback(
    async (item) => {
      if (!item) return;
      if (item.type === "photo" && item.storagePath) {
        try {
          await deleteObject(storageRef(storage, item.storagePath));
        } catch (err) {
          console.warn("[useDesignItems] storage delete failed", err);
        }
      }
      setItems((prev) => (prev || []).filter((it) => it.id !== item.id));
    },
    [setItems]
  );

  return { items: items || [], loaded, addPhoto, addLink, updateItem, attachImage, removeItem };
}
